/* UI */
import { Box } from '@auviknetworks/iceberg-ui';
import { Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import { hmsFormatter } from 'application/Shared/Helpers';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

interface UsageOverviewType {
    name: string;
    [key: string]: string | number | undefined;
}

export const UsageOverview = ({
    renderingScheduledReport,
    reportData
}: {
    renderingScheduledReport: boolean;
    reportData: UsageOverviewType[];
}) => {
    const { t } = useTranslation();
    const { palette } = useTheme();

    // Get the name of the top 5 apps with the most usage (+ "Other Applications",
    // which is the accumulated value of the rest of the apps).
    // The "name" entry is the date in the format MM/DD/YY, that is why we exclude it
    const usageReportKeys = Array.from(
        reportData.reduce((acc, report) => {
            Object.keys(report).forEach((key) => {
                if (key !== 'name') acc.add(key);
            });
            return acc;
        }, new Set<string>())
    );

    return (
        <Box marginY="24px">
            <Typography variant="h5">
                {t('Reports.UsageReport.usage_overview')}
            </Typography>
            <Divider
                sx={{
                    borderBottomWidth: '4px',
                    borderColor: 'border.darkerDivider',
                    marginBottom: '16px',
                    marginTop: '8px'
                }}
            />
            <ResponsiveContainer height={300} width="100%">
                <BarChart
                    data={reportData}
                    margin={{
                        bottom: 5,
                        left: 35,
                        right: 20,
                        top: 10
                    }}
                >
                    <CartesianGrid
                        stroke={palette.chart.axis}
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={hmsFormatter} />
                    <Tooltip formatter={hmsFormatter} />
                    <Legend
                        iconSize={10}
                        layout="horizontal"
                        align="center"
                        wrapperStyle={{
                            fontSize: FontSizes[0],
                            top: '275px'
                        }}
                    />
                    {usageReportKeys.map((row: string, index: number) => (
                        <Bar
                            isAnimationActive={!renderingScheduledReport}
                            key={`iuo_${row}`}
                            dataKey={row}
                            stackId="a"
                            fill={
                                palette.graph.colors[
                                    index % palette.graph.colors.length
                                ]
                            }
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};
